
import { useRef } from 'react';
import { X, Upload } from 'lucide-react';
import { SketchPicker } from 'react-color';
import { showToast } from '../../../../../helper/ShowToast';

export const CustomizeStep = ({ preSaveLinkData, setPreSaveLinkData, artistInfo }) => {
    const fileInputRef = useRef(null);

    const openBgFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handleBgImageChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        if (!file.type.startsWith("image/")) {
            showToast('warning', "Please select a valid image file")
            return;
        }

        const previewURL = URL.createObjectURL(file);

        setPreSaveLinkData(prev => ({
            ...prev,
            bgImageFile: file,
            bgImageFileName: file.name,
            bgImagePreview: previewURL,
            bgImageChanged: true,
        }));
    };


    const removeBgImage = () => {
        if (preSaveLinkData?.bgImagePreview) URL.revokeObjectURL(preSaveLinkData.bgImagePreview);
        setPreSaveLinkData({ ...preSaveLinkData, bgImageFile: null, bgImageFileName: "", bgImagePreview: "" })
    };

    const handleInputChange = (field, value) => {
        setPreSaveLinkData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="space-y-5" data-tour="profile-form">
            <div>
                <h2 className="text-xl font-bold text-theme_clr1 mb-1">Customize your Pre-save Page</h2>
                <p className="text-theme_clr2 font-semibold text-sm">Start customizing your pre-save page.</p>
            </div>
            <div>
                <label className="block text-sm font-bold text-theme_clr1 mb-2">Release Title</label>
                <p className="text-xs text-theme_clr2 font-semibold mb-1">Enter the name of your song, EP, or album.</p>
                <input
                    type="text"
                    value={preSaveLinkData.releaseTitle}
                    placeholder='Song, EP, or album name'
                    onChange={(e) => handleInputChange('releaseTitle', e.target.value)}
                    className="w-full px-4 py-2 text-theme_clr1 border border-theme_clr1 rounded-lg focus:outline-none focus:border-2 focus:border-theme_clr1"
                />
            </div>
            <div>
                <label className="block text-sm font-bold text-theme_clr1 mb-2">Pre-Save Subtitle</label>
                <p className="text-xs text-theme_clr2 font-semibold mb-1">This appears below your release title before your release goes live.</p>
                <input
                    type="text"
                    value={preSaveLinkData?.preReleaseDescription}
                    placeholder='Subtitle shown before release'
                    onChange={(e) => handleInputChange('preReleaseDescription', e.target.value)}
                    className="w-full px-4 py-2 text-theme_clr1 border border-theme_clr1 rounded-lg focus:outline-none focus:border-2 focus:border-theme_clr1"
                />
            </div>
            <div>
                <label className="block text-sm font-bold text-theme_clr1 mb-2">Post-Release Subtitle</label>
                <p className="text-xs text-theme_clr2 font-semibold mb-1">This appears below your release title after your release goes live.</p>
                <input
                    type="text"
                    value={preSaveLinkData?.description}
                    placeholder='Subtitle shown after release'
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full px-4 py-2 text-theme_clr1 border border-theme_clr1 rounded-lg focus:outline-none focus:border-2 focus:border-theme_clr1"
                />
            </div>
            <div>
                <label className="block text-sm font-bold text-theme_clr1 mb-2">
                    Artwork
                </label>

                <p className="text-sm text-theme_clr2 font-semibold mb-3">
                    Select the artwork you want to display along with your pre-save.
                    We recommend at least 1000×1000.
                </p>

                <div className="flex items-center gap-4">
                    {/* IMAGE PREVIEW */}
                    <img
                        src={
                            preSaveLinkData.imageUrl ||
                            artistInfo?.avatar ||
                            "/placeholder.png"
                        }
                        alt="Artwork"
                        className="w-20 h-20 rounded object-cover border"
                    />

                    {/* CHANGE BUTTON */}
                    <label className="bg-theme_clr2 text-white px-6 py-2 rounded font-semibold cursor-pointer hover:bg-orange-600 transition">
                        CHANGE
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;

                                const previewUrl = URL.createObjectURL(file);

                                setPreSaveLinkData(prev => ({
                                    ...prev,
                                    imageSource: "uploaded",
                                    imageUrl: previewUrl,
                                    imageFile: file,
                                    imageFileName: file.name,
                                    imageChanged: true,
                                }));
                            }}
                        />
                    </label>

                    {/* RESET TO ARTIST IMAGE */}
                    {preSaveLinkData.imageSource === "uploaded" && (
                        <button
                            type="button"
                            onClick={() =>
                                setPreSaveLinkData(prev => ({
                                    ...prev,
                                    imageSource: "artist",
                                    imageUrl: artistInfo?.avatar || "",
                                    imageFile: null,
                                    imageFileName: ""
                                }))
                            }
                            className="text-sm font-semibold text-theme_clr2 underline"
                        >
                            Use artist image
                        </button>
                    )}
                </div>
            </div>


            <hr className="border-t border-theme_clr2 my-8" />

            <div className="chip-card-wrapper flex flex-col gap-4 mt-2" data-tour="page-layout">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="font-bold sm:min-w-[110px] text-theme_clr1 text-sm sm:text-base">Layout</div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3 flex-1">
                        <button
                            type="button"
                            className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                ${preSaveLinkData?.pageLayout === "modern_layout"
                                    ? "border-theme_clr2 bg-theme_clr2 text-white"
                                    : "border-theme_clr1 text-theme_clr1"}`}
                            onClick={() => handleInputChange('pageLayout', "modern_layout")}
                        >
                            Modern
                        </button>
                        <button
                            type="button"
                            className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                ${preSaveLinkData?.pageLayout === "action_page"
                                    ? "border-theme_clr2 bg-theme_clr2 text-white"
                                    : "border-theme_clr1 text-theme_clr1"}`}
                            onClick={() => handleInputChange('pageLayout', "action_page")}
                        >
                            Advanced
                        </button>
                        <button
                            type="button"
                            className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                ${preSaveLinkData?.pageLayout === "base_layout"
                                    ? "border-theme_clr2 bg-theme_clr2 text-white"
                                    : "border-theme_clr1 text-theme_clr1"}`}
                            onClick={() => handleInputChange('pageLayout', "base_layout")}
                        >
                            Base
                        </button>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="font-bold sm:min-w-[110px] text-theme_clr1 text-sm sm:text-base">Background</div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3 flex-1">
                        <button
                            type="button"
                            className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                ${preSaveLinkData?.selectedBackground === "blur"
                                    ? "border-theme_clr2 bg-theme_clr2 text-white"
                                    : "border-theme_clr1 text-theme_clr1"}`}
                            onClick={() => handleInputChange('selectedBackground', "blur")}
                        >
                            Blur
                        </button>
                        <button
                            type="button"
                            className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                ${preSaveLinkData?.selectedBackground === "color"
                                    ? "border-theme_clr2 bg-theme_clr2 text-white"
                                    : "border-theme_clr1 text-theme_clr1"}`}
                            onClick={() => handleInputChange('selectedBackground', "color")}
                        >
                            Color
                        </button>
                        <button
                            type="button"
                            className={`border flex items-center justify-center rounded-lg px-2 py-2.5 sm:py-3 min-h-[44px] cursor-pointer transition-all duration-200 font-bold text-sm sm:text-base
                                ${preSaveLinkData?.selectedBackground === "image"
                                    ? "border-theme_clr2 bg-theme_clr2 text-white"
                                    : "border-theme_clr1 text-theme_clr1"}`}
                            onClick={() => handleInputChange('selectedBackground', "image")}
                        >
                            Image
                        </button>
                    </div>
                </div>
                <div className="flex flex-wrap justify-center items-center gap-2">
                    {preSaveLinkData?.selectedBackground === "image" && (
                        <div className="w-full max-w-full bg-white rounded-2xl shadow-xl p-8">

                            {/* Hidden input MUST be here */}
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleBgImageChange}   //This calls handleBgImageChange
                            />

                            {/* IF NO IMAGE SELECTED */}
                            {!preSaveLinkData?.bgImagePreview && (
                                <div
                                    className="flex flex-col items-center justify-center w-full h-64 
                                                                   border-2 border-dashed border-gray-300 rounded-xl cursor-pointer 
                                                                     hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-300"
                                    onClick={openBgFilePicker}    // opens file dialog
                                >
                                    <Upload className="w-12 h-12 text-gray-400 mb-3" />
                                    <p className="mb-2 text-sm text-gray-500">
                                        <span className="font-semibold">Click to upload</span> or drag & drop
                                    </p>
                                    <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                                </div>
                            )}

                            {/* IF IMAGE SELECTED */}
                            {preSaveLinkData?.bgImagePreview && (
                                <div className="relative">
                                    <div className="w-full h-64 rounded-xl overflow-hidden bg-gray-100">
                                        <img
                                            src={preSaveLinkData?.bgImagePreview}
                                            alt="Background"
                                            className="w-full h-full object-contain"
                                        />
                                    </div>

                                    <button
                                        onClick={removeBgImage}
                                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 
                                                    text-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>

                                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-600 truncate">
                                            <span className="font-semibold">File:</span> {preSaveLinkData?.bgImageFileName}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {preSaveLinkData?.selectedBackground === "color" &&
                        <div>
                            <SketchPicker
                                color={preSaveLinkData?.selectedColor}
                                onChangeComplete={(e) => handleInputChange('selectedColor', e.hex)}
                            />
                        </div>
                    }
                </div>
            </div>
            <div>
                <label className="block text-sm font-bold text-theme_clr1 mb-1">After pre-saving send fans to a custom webpage.</label>
                <input
                    type="text"
                    placeholder="URL"
                    value={preSaveLinkData?.afterPresaveRedirectUrl}
                    className="w-full mt-3 px-4 py-2 placeholder-gray-500 border border-theme_clr1 rounded-lg focus:outline-none focus:border-2 focus:border-theme_clr1"
                    onChange={(e) => handleInputChange('afterPresaveRedirectUrl', e.target.value)}
                />
            </div>
        </div>
    )
}